import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { createParser, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { usePolicyHistoryChartsQuery } from '../../../../../queries/Policies/usePolicyHistoryChartsQuery';
import { Alert, AlertDescription, AlertTitle } from '../../../../components-v2/Alert';
import { styleSheet as advancedReportingStyleSheet } from '../../styles';
import { IPolicyFilterPeriod, PoliciesFilters } from '../components/PoliciesFilters';
import { usePolicyFilterCriteria } from '../usePolicyFilterCriteria';
import { BookMovement } from './BookMovement';
import {
	firstFullMonth,
	getHistoryStage,
	HISTORY_CHARTS,
	readInForce,
	readMovement,
	readProducerMatrix,
	readRetention,
	toHistoryFilter,
} from './historyData';
import { HistoryKpis } from './HistoryKpis';
import { NewBusinessByOriginationChart } from './NewBusinessByOriginationChart';
import { ProducerMatrix } from './ProducerMatrix';
import { styleSheet } from './styles';

const NO_CRITERIA: Api.IInsurancePolicyFilterCriteria[] = [];
const PRODUCER_MONTHS = 6;

const dayParser = createParser({
	parse: (value: string) => (moment(value, 'YYYY-MM-DD', true).isValid() ? value : null),
	serialize: (value: string) => value,
});

const periodParsers = {
	historyFrom: dayParser,
	historyTo: dayParser,
};

export const BookHistoryTab = ({
	trackingSince,
	baselineCompleted,
}: {
	trackingSince?: string;
	baselineCompleted?: string;
}) => {
	const { normalizedCriteria, handleFilterChange } = usePolicyFilterCriteria('historyFilterCriteria', NO_CRITERIA);
	const [{ historyFrom, historyTo }, setPeriodParams] = useQueryStates(periodParsers);
	const period = useMemo<IPolicyFilterPeriod>(() => ({ from: historyFrom, to: historyTo }), [historyFrom, historyTo]);
	const handlePeriodChange = useCallback(
		(change: Partial<IPolicyFilterPeriod>) =>
			setPeriodParams({
				...('from' in change && { historyFrom: change.from || null }),
				...('to' in change && { historyTo: change.to || null }),
			}),
		[setPeriodParams]
	);

	const stage = getHistoryStage({ baselineCompleted });
	const filter = useMemo(() => toHistoryFilter(normalizedCriteria, period), [normalizedCriteria, period]);
	const historyQuery = usePolicyHistoryChartsQuery({
		enabled: stage !== 'noSnapshot',
		filter,
		charts: HISTORY_CHARTS,
	});
	const response = historyQuery.data;
	const loading = historyQuery.isLoading;

	const inForce = useMemo(() => readInForce(response?.charts?.inForce), [response]);
	const policyHolders = useMemo(() => readInForce(response?.charts?.policyHolders), [response]);
	const movement = useMemo(() => readMovement(response), [response]);
	const retention = useMemo(() => readRetention(response), [response]);
	const producers = useMemo(
		() => readProducerMatrix(response?.charts?.newBusinessByProducer, PRODUCER_MONTHS),
		[response]
	);

	const firstMonth = stage === 'justStarted' && baselineCompleted ? firstFullMonth(baselineCompleted) : null;
	const firstMonthOn = firstMonth?.format('MMM D, YYYY');
	const formatDay = (value?: string) => (value ? moment(value).format('MMM D, YYYY') : null);
	const trackingNote =
		stage === 'noSnapshot'
			? trackingSince && `Tracking switched on ${formatDay(trackingSince)}.`
			: stage === 'justStarted'
				? `History started ${formatDay(baselineCompleted)}. Each month adds a point from here.`
				: `Tracking since ${formatDay(baselineCompleted)}. History before then isn’t available.`;

	return (
		<>
			<div className={css(advancedReportingStyleSheet.filterSection)}>
				<div className={css(advancedReportingStyleSheet.filterHeader)}>
					<PoliciesFilters
						variant='history'
						filterCriteria={normalizedCriteria}
						onFilterChange={handleFilterChange}
						period={period}
						onPeriodChange={handlePeriodChange}
					/>
				</div>
				{trackingNote ? <p className={css(styleSheet.trackingSince)}>{trackingNote}</p> : null}
			</div>

			{historyQuery.isError ? (
				<Alert tone='warning'>
					<AlertTitle>Book history isn’t available yet</AlertTitle>
					<AlertDescription>
						The history service didn’t respond for this account. New business by origination month below still works.
					</AlertDescription>
				</Alert>
			) : stage === 'noSnapshot' ? (
				<Alert>
					<AlertTitle>Book history is getting started</AlertTitle>
					<AlertDescription>
						It records its first snapshot of this book the next time a policy changes (an import, a sync or an edit),
						then adds a point every month. Premium in force, book movement and premium by producer appear from there.
					</AlertDescription>
				</Alert>
			) : (
				<>
					<HistoryKpis
						inForce={inForce}
						policyHolders={policyHolders}
						retention={retention}
						loading={loading}
						changeFrom={firstMonth?.format('MMM YYYY')}
					/>
					<BookMovement rows={movement} loading={loading} firstMonthOn={firstMonthOn} />
					<ProducerMatrix
						months={producers.months}
						rows={producers.rows}
						loading={loading}
						firstMonthOn={firstMonthOn}
					/>
				</>
			)}

			<NewBusinessByOriginationChart criteria={normalizedCriteria} />
		</>
	);
};
